import { m } from 'framer-motion'
import { FileSearch, FlaskConical, Lightbulb, Sparkles, Wrench } from 'lucide-react'
import { useMemo } from 'react'
import { VIBE_AGENT_TEMPLATE_PROMPTS } from 'v2/shared/constants/vibe-agent-prompts'
import { toTestId } from '../constants'
import { welcomeContainer, welcomeItem } from '../motion'
import { useVibeAgentStore } from '../store'

interface PromptSuggestion {
  icon: React.ReactNode
  label: string
  prompt: string
}

// Chips follow the welcome text (transcript-list empty state), settling
// left-to-right after it.
const chipsContainer = welcomeContainer(0.15)

const EXISTING_FLOW_SUGGESTIONS: PromptSuggestion[] = [
  {
    icon: <FileSearch className="h-3.5 w-3.5" />,
    label: 'Explain the current flow',
    prompt: 'Explain what my current flow does step by step.',
  },
  {
    icon: <Lightbulb className="h-3.5 w-3.5" />,
    label: 'Suggest improvements',
    prompt:
      'Review my current flow and suggest improvements for better performance and reliability.',
  },
  {
    icon: <Wrench className="h-3.5 w-3.5" />,
    label: 'Add error handling',
    prompt: 'Add proper error handling to my flow to handle edge cases gracefully.',
  },
  {
    icon: <Sparkles className="h-3.5 w-3.5" />,
    label: 'Optimize the flow',
    prompt: 'Analyze my flow and optimize it for better efficiency and reduced latency.',
  },
  {
    icon: <FlaskConical className="h-3.5 w-3.5" />,
    label: 'Test this agent',
    prompt:
      'Run simulation tests against this agent to verify it handles conversations correctly and meets all goals.',
  },
]

export const VibePromptSuggestions = ({
  variant = 'existing',
}: {
  variant?: 'existing' | 'new'
}) => {
  const setUserInput = useVibeAgentStore((state) => state.setUserInput)

  const suggestions = useMemo<PromptSuggestion[]>(
    () =>
      variant === 'new'
        ? VIBE_AGENT_TEMPLATE_PROMPTS.map((p) => ({
            icon: <p.icon className="h-3.5 w-3.5" />,
            label: p.title,
            prompt: p.prompt,
          }))
        : EXISTING_FLOW_SUGGESTIONS,
    [variant],
  )

  return (
    <m.div
      className="flex gap-2 mt-4 overflow-x-auto hide-scrollbar"
      variants={chipsContainer}
      initial="hidden"
      animate="visible"
    >
      {suggestions.map((suggestion) => (
        <m.button
          key={suggestion.label}
          variants={welcomeItem}
          type="button"
          title={suggestion.prompt}
          onClick={() => setUserInput(suggestion.prompt)}
          className="group flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-st2 bg-bg2/50 font-inter text-s-400 normal-case tracking-normal text-muted-foreground hover:border-primary/50 hover:bg-primary/5 hover:text-foreground transition-[color,background-color,border-color] duration-150 whitespace-nowrap flex-shrink-0"
          data-testid={`prompt-suggestion-${toTestId(suggestion.label)}`}
        >
          <span className="text-muted-foreground/70 group-hover:text-primary transition-colors">
            {suggestion.icon}
          </span>
          <span>{suggestion.label}</span>
        </m.button>
      ))}
    </m.div>
  )
}
